"use client";

import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import Image from "next/image";
import type { MediaObjectFit } from "@/utilities/mediaObjectFit";
import { mediaObjectFitClassName } from "@/utilities/mediaObjectFit";

type LogoItem = {
  src: string;
  alt?: string;
  title?: string;
  multiplier?: number;
  objectFit?: MediaObjectFit;
};

type LogoMarqueeRowProps = {
  direction?: "left" | "right";
  speed?: number;
  logos: LogoItem[];
  rowId: string;
};

const MIN_DUPLICATE_SETS = 2;

const createLogoTrack = (logos: LogoItem[], duplicateSets: number) =>
  Array.from({ length: duplicateSets }, (_, setIndex) =>
    logos.map((logoItem, index) => ({
      ...logoItem,
      key: `logo-${setIndex}-${logoItem.src}-${index}`,
      ariaHidden: setIndex > 0,
    })),
  ).flat();

export function LogoMarqueeRow({
  direction = "left",
  speed,
  logos,
  rowId,
}: LogoMarqueeRowProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [duplicateSets, setDuplicateSets] = useState(3);
  const [shift, setShift] = useState("0px");

  useEffect(() => {
    const updateTrackSize = () => {
      const viewportWidth = viewportRef.current?.offsetWidth ?? 0;
      const logoSetWidth = measureRef.current?.offsetWidth ?? 0;

      if (!logoSetWidth) return;

      const nextDuplicateSets = Math.max(
        MIN_DUPLICATE_SETS,
        Math.ceil(viewportWidth / logoSetWidth) + 1,
      );

      setDuplicateSets((current) =>
        current === nextDuplicateSets ? current : nextDuplicateSets,
      );
      setShift(`-${logoSetWidth}px`);
    };

    updateTrackSize();

    const resizeObserver = new ResizeObserver(() => {
      updateTrackSize();
    });

    if (viewportRef.current) resizeObserver.observe(viewportRef.current);
    if (measureRef.current) resizeObserver.observe(measureRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [logos]);

  const logoTrack = useMemo(
    () => createLogoTrack(logos, duplicateSets),
    [duplicateSets, logos],
  );

  const animationDuration = typeof speed === "number" && speed > 0 ? speed : 10;
  const animationName =
    direction === "right" ? "logo-section-marquee-right" : "logo-section-marquee";

  if (!logoTrack.length) return null;

  return (
    <div
      ref={viewportRef}
      className="relative w-full overflow-hidden mask-[linear-gradient(to_right,transparent_0%,black_10%,black_90%,transparent_100%)]"
    >
      <div
        className="flex w-max items-center gap-4 md:gap-6"
        style={
          {
            "--logo-row-shift": shift,
            animationName,
            animationDuration: `${animationDuration}s`,
            animationTimingFunction: "linear",
            animationIterationCount: "infinite",
            willChange: "transform",
          } as CSSProperties
        }
      >
        {logoTrack.map((logoItem) => {
          const multiplier =
            typeof logoItem.multiplier === "number" && logoItem.multiplier > 0
              ? logoItem.multiplier
              : 1;

          return (
            <div
              key={`${rowId}-${logoItem.key}`}
              aria-hidden={logoItem.ariaHidden}
              className="flex shrink-0 items-center justify-center rounded-2xl bg-background/70 px-4"
              style={{ width: `${6 * multiplier}rem` }}
            >
              <Image
                src={logoItem.src}
                alt={logoItem.alt || ""}
                title={logoItem.title || logoItem.alt || undefined}
                width={160}
                height={80}
                className={`h-auto w-full opacity-70 ${mediaObjectFitClassName(logoItem.objectFit ?? "contain")}`}
                // Eager: lazy logos pop in as the track scrolls sideways.
                // Low priority: React skips the SSR preload, so logos do not
                // compete with the hero image.
                loading="eager"
                fetchPriority="low"
                decoding="async"
                unoptimized
              />
            </div>
          );
        })}
      </div>

      <div
        ref={measureRef}
        aria-hidden="true"
        className="pointer-events-none absolute invisible flex w-max items-center gap-4 md:gap-6"
      >
        {logos.map((logoItem, index) => {
          const multiplier =
            typeof logoItem.multiplier === "number" && logoItem.multiplier > 0
              ? logoItem.multiplier
              : 1;

          return (
            <div
              key={`${rowId}-measure-${logoItem.src}-${index}`}
              className="flex shrink-0 items-center justify-center rounded-2xl bg-background/70 px-4"
              style={{ width: `${6 * multiplier}rem` }}
            >
              <Image
                src={logoItem.src}
                alt=""
                width={160}
                height={80}
                className={`h-auto w-full opacity-70 ${mediaObjectFitClassName(logoItem.objectFit ?? "contain")}`}
                loading="eager"
                fetchPriority="low"
                decoding="async"
                unoptimized
              />
            </div>
          );
        })}
      </div>

      <style jsx global>{`
        @keyframes logo-section-marquee {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(var(--logo-row-shift));
          }
        }

        @keyframes logo-section-marquee-right {
          from {
            transform: translateX(var(--logo-row-shift));
          }
          to {
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
}
